/**
 * i18n provider. Wraps `@4f/i18n`'s pure translator over an already-loaded
 * message tree (loading/detection is the app's job — this layer is
 * presentation only) and exposes a `t(key, params?)` accessor via `useT()`. No
 * user-facing string literals live in components; they pull from here.
 */
import type { Locale, MessageTree, TranslationParams } from '@4f/i18n';
import { DEFAULT_LOCALE, translate } from '@4f/i18n';
import { createContext, type ParentComponent, useContext } from 'solid-js';
import { MissingProviderError } from '../errors/index.ts';

/** The bound translator components call. */
export type Translator = (key: string, params?: TranslationParams) => string;

export interface I18nContextValue {
  /** Look up `key` (dot path) and interpolate `{params}`. */
  readonly t: Translator;
  /** The active locale. */
  readonly locale: Locale;
}

const I18nContext = createContext<I18nContextValue>();

export interface I18nProviderProps {
  /** Already-loaded messages for the active namespace(s). */
  readonly messages: MessageTree;
  /** Active locale. Defaults to the i18n default locale. */
  readonly locale?: Locale;
  /** Default-locale backfill for keys missing from `messages`. */
  readonly fallback?: MessageTree;
}

export const I18nProvider: ParentComponent<I18nProviderProps> = (props) => {
  // `t` reads `props` live so a namespace/locale swap re-translates without a
  // new provider; `locale` is a getter for the same reason.
  const value: I18nContextValue = {
    t: (key, params) => translate(props.messages, key, { fallback: props.fallback, params }),
    get locale(): Locale {
      return props.locale ?? DEFAULT_LOCALE;
    },
  };

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
};

/** Access the translator. Throws when used outside {@link I18nProvider}. */
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new MissingProviderError('useT', 'I18nProvider');
  return ctx;
}
